import { movies } from '@/mocks/movies';
import type { Movie, MovieCategory } from '@/types/movie';

export function getMovieById(id: string): Movie | undefined {
  return movies.find((movie) => movie.id === id);
}

export function getMoviesByCategory(category: MovieCategory): Movie[] {
  return movies.filter((movie) => movie.category === category);
}

export function getFeaturedMovies(): Movie[] {
  return movies.filter((movie) => movie.featured);
}

export function getPopularMovies(limit = 8): Movie[] {
  return [...movies]
    .sort((a, b) => b.views + b.favorites * 2 - (a.views + a.favorites * 2))
    .slice(0, limit);
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.category === movie.category) score += 5;
      score += item.tags.filter((tag) => movie.tags.includes(tag)).length * 2;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .map((entry) => entry.item);
  return scored.slice(0, limit);
}

export interface MovieFilters {
  category?: MovieCategory | '全部';
  yearRange?: 'all' | 'recent3' | 'recent5';
  minRating?: number;
  durationRange?: 'all' | 'short' | 'medium' | 'long';
}

export function filterMovies(list: Movie[], filters: MovieFilters): Movie[] {
  const currentYear = new Date().getFullYear();
  return list.filter((movie) => {
    if (filters.category && filters.category !== '全部' && movie.category !== filters.category) {
      return false;
    }
    if (filters.yearRange === 'recent3' && movie.year < currentYear - 3) return false;
    if (filters.yearRange === 'recent5' && movie.year < currentYear - 5) return false;
    if (typeof filters.minRating === 'number' && movie.rating < filters.minRating) return false;
    if (filters.durationRange === 'short' && movie.duration >= 90) return false;
    if (filters.durationRange === 'medium' && (movie.duration < 90 || movie.duration > 120)) return false;
    if (filters.durationRange === 'long' && movie.duration <= 120) return false;
    return true;
  });
}

export type SortKey = 'latest' | 'popular' | 'rating';

export function sortMovies(list: Movie[], key: SortKey): Movie[] {
  const copy = [...list];
  if (key === 'latest') {
    copy.sort((a, b) => b.year - a.year || b.rating - a.rating);
  } else if (key === 'rating') {
    copy.sort((a, b) => b.rating - a.rating);
  } else {
    copy.sort((a, b) => b.views - a.views);
  }
  return copy;
}

export function searchMovies(keyword: string, category?: MovieCategory | '全部'): Movie[] {
  const q = keyword.trim().toLowerCase();
  return movies.filter((movie) => {
    const matchCategory = !category || category === '全部' || movie.category === category;
    if (!matchCategory) return false;
    if (!q) return true;
    return (
      movie.title.toLowerCase().includes(q) ||
      movie.synopsis.toLowerCase().includes(q) ||
      movie.director.toLowerCase().includes(q) ||
      movie.tags.some((tag) => tag.toLowerCase().includes(q))
    );
  });
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}